<template>
  <v-dialog
    v-model="model"
    style="height: 100%"
    max-width="100%"
    scrollable
    fullscreen
    :scrim="false"
    transition="dialog-bottom-transition"
    class="dialogReport"
  >
    <v-card v-if="employee">
      <v-card-title class="order-history-header">
        <v-row no-gutters align="center">
          <img
            src="https://restorani.bitlab.host/img/o.png"
            alt="image"
            style="width: 54px"
          />
          <div style="font-size: 15px; font-weight: 700; padding-left: 20px">
            {{ toLatin(employee.name) }}
            {{ toLatin(employee.lastname) }}, ID
            {{ employee.id }}
          </div>
        </v-row>
        <v-spacer></v-spacer>
        <circle-button @click="model = false" icon="mdi-close"></circle-button>
      </v-card-title>
      <v-card-text class="py-4">
        <v-row>
          <v-col>
            <month-picker
              v-model="date"
              @updatedMonthDates="monthDates = $event"
            ></month-picker>
          </v-col>
          <v-col>
            <text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              :label="$t('search')"
              single-line
              hide-details
            ></text-field>
          </v-col>
          <v-col>
            <v-checkbox
              class="show-all-checkbox"
              :label="$t('show_all_days')"
              hide-details
              v-model="showAllDates"
            >
            </v-checkbox>
          </v-col>
          <v-col cols="auto">
            <dropdown
              :label="$t('view')"
              v-model="selectedLayout"
              :items="layouts"
              hide-details
            ></dropdown>
          </v-col>
          <v-col cols="auto">
            <print-button @click="print"></print-button>
          </v-col>
        </v-row>
        <v-data-table
          :headers="headers"
          :items="currentDisplayedOrders"
          :search="search"
          :rows-per-page="[100]"
          :items-per-page="100"
          hide-default-footer
          class="ordera-box-shadow striped-table"
        >
          <template v-slot:item="{ item }">
            <tr>
              <td>{{ item.date }}</td>
              <td>{{ item.shift }}</td>
              <template v-for="category in mealCategories">
                <td>
                  <span v-if="Array.isArray(item[category]) && item[category].length">
                    <template v-for="meal in item[category]">
                      <!-- Layout 0: Količine i nazive jela -->
                      <template v-if="selectedLayout === 0">
                        <strong>{{ meal.quantity }}x</strong>
                        <v-tooltip bottom v-if="meal.label !== $t('total')">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                              {{ toLatin(meal.name) }}
                            </span>
                          </template>
                          <span>{{ meal.id }}</span>
                        </v-tooltip>
                        <span v-else>{{ meal.label }}</span>
                        <br v-if="item[category].indexOf(meal) < item[category].length - 1" />
                      </template>
                      <!-- Layout 1: Nazive jela -->
                      <template v-else-if="selectedLayout === 1">
                        <v-tooltip bottom v-if="meal.label !== $t('total')">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                              {{ toLatin(meal.name) }}
                            </span>
                          </template>
                          <span>{{ meal.id }}</span>
                        </v-tooltip>
                        <span v-else>{{ meal.label }}</span>
                        <br v-if="item[category].indexOf(meal) < item[category].length - 1" />
                      </template>
                      <!-- Layout 2: Količine jela -->
                      <template v-else-if="selectedLayout === 2">
                        <strong>{{ meal.quantity }}</strong>
                        <br v-if="item[category].indexOf(meal) < item[category].length - 1" />
                      </template>
                      <!-- Layout 3: Default fallback -->
                      <template v-else>
                        {{ meal.label }}
                        <br v-if="item[category].indexOf(meal) < item[category].length - 1" />
                      </template>
                    </template>
                  </span>
                </td>
              </template>
              <td>{{ item.total }}</td>
            </tr>
          </template>
          <template v-slot:body.append>
            <tr>
              <td style="font-weight: bold" colspan="2">{{ $t("total") }}</td>
              <template v-for="category in mealCategories">
                <td style="font-weight: bold">
                  <span v-if="totalByCategory[category]">
                    {{ totalByCategory[category] || 0 }}x
                  </span>
                </td>
              </template>
              <td style="font-weight: bold">
                {{ totalQuantity }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import reportService from "@/services/report"; // Add this import
import moment from "moment";
import hasModel from "@/mixins/hasModel";
import hasPrinting from "@/mixins/hasPrinting";
import hasFoodOrders from "@/mixins/report/hasFoodOrders";
import hasSnackbar from "@/mixins/hasSnackbar";

export default {
  name: "ReportEmployeeOrderHistoryDialog",
  mixins: [hasModel, hasPrinting, hasFoodOrders, hasSnackbar],
  props: {
    employee: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      date: new Date().toISOString().substr(0, 7),
      search: "",
      ordersByDates: [],
      monthDates: [],
      showAllDates: false,
      selectedLayout: 0,
      errorLoadingReport: false, // For error handling
    };
  },
  created() {
    this.date = this.filters.dates.to;
  },
  computed: {
    layouts() {
      return [
        { text: this.$t("quantities_and_names"), value: 0 },
        { text: this.$t("names_only"), value: 1 },
        { text: this.$t("quantities_only"), value: 2 },
        { text: this.$t("spare_meals"), value: 3 },
      ];
    },
    currentDisplayedOrders() {
      return this.showAllDates ? this.ordersByDatesEntireMonth : this.ordersByDates;
    },
    ordersByDatesEntireMonth() {
      const ordersMap = new Map(
        this.ordersByDates.map((order) => [order.dateFull, order])
      );

      return this.monthDates.map((dateStr) => {
        if (ordersMap.has(dateStr)) {
          return ordersMap.get(dateStr);
        }
        const defaultEntry = {
          date: moment(dateStr).format("D."),
          shift: null,
          total: 0,
          dateFull: dateStr,
        };
        this.mealCategories.forEach((category) => {
          defaultEntry[category] = [];
        });
        return defaultEntry;
      });
    },
    topbarBackground() {
      return this.$store.state.restaurant?.settings?.topbar?.background;
    },
    filters() {
      return this.$store.state.report.filters;
    },
    month() {
      return parseInt(this.date.split("-")[1]);
    },
    year() {
      return parseInt(this.date.split("-")[0]);
    },
    monthName() {
      return moment(this.month, "M").format("MMMM").toUpperCase();
    },
    headers() {
      return [
        { text: this.$t("date"), value: "date" },
        { text: this.$t("shift.shift"), value: "shift " },
        ...this.mealCategories.map((category) => ({
          text: this.translate(category),
          value: category,
        })),
        { text: this.$t("quantity"), value: "total" },
      ];
    },
    printItems() {
      return this.currentDisplayedOrders.map((item) => {
        const newItem = { ...item };
        this.mealCategories.forEach((category) => {
          if (Array.isArray(item[category])) {
            // Layout 0: Količine i nazive jela
            if (this.selectedLayout === 0) {
              newItem[category] = item[category]
                .map((meal) => `${meal.quantity}x ${meal.name}`)
                .join(", ");
            }
            // Layout 1: Nazive jela
            else if (this.selectedLayout === 1) {
              newItem[category] = item[category]
                .map((meal) => meal.name)
                .join(", ");
            }
            // Layout 2: Količine jela
            else if (this.selectedLayout === 2) {
              newItem[category] = item[category]
                .map((meal) => meal.quantity)
                .join(", ");
            }
            // Default fallback
            else {
              newItem[category] = item[category].map((meal) => meal.label).join(", ");
            }
          }
        });
        return newItem;
      });
    },
    totalByCategory() {
      const totals = {};
      this.mealCategories.forEach((category) => {
        totals[category] = this.currentDisplayedOrders.reduce((sum, row) => {
          const arr = row[category];
          if (Array.isArray(arr)) {
            return (
              sum +
              arr.reduce((catSum, meal) => {
                return catSum + (meal.quantity || 0);
              }, 0)
            );
          }
          return sum;
        }, 0);
      });
      return totals;
    },
    totalQuantity() {
      return this.currentDisplayedOrders.reduce(
        (sum, row) => sum + (parseInt(row.total) || 0),
        0
      );
    },
  },
  methods: {
    print() {
      this.printDocument({
        tables: [
          {
            headers: this.headers,
            items: this.printItems,
          },
        ],
        title: `${this.toLatin(this.employee.name)} ${this.toLatin(
          this.employee.lastname
        )}`,
        subtitle: [this.monthDates[0], this.monthDates.slice(-1)[0]]
          .map((date) => {
            return this.localeDate(date);
          })
          .join(" - "),
      });
    },
    async loadReport() {
      if (!this.employee || !this.employee.id) return;
      this.loading = true;
      this.ordersByDates = [];
      this.errorLoadingReport = false;
      try {
        // Use reportService for the API call
        const response = await reportService.getEmployeeOrdersByMonth({
          month: this.month,
          year: this.year,
          user_id: this.employee.id,
        });
        const data = response.data;
        const uniqueDates = [...new Set(data.map((item) => item.date))];
        this.setMealCategories(data); // Assuming this method is from a mixin and handles its own logic

        this.ordersByDates = uniqueDates.map((dateStr) => {
          const dayOrders = data.filter((item) => item.date === dateStr);
          const dailyOrders = {
            date: moment(dateStr).format("D."),
            shift: dayOrders[0]?.shift,
            total: dayOrders.reduce((sum, item) => sum + (item.quantity || 0), 0),
            dateFull: dateStr,
          };
          this.mealCategories.forEach((category) => {
            dailyOrders[category] = this.getDailyDish(dayOrders, category); // Assuming this method is from a mixin
          });
          return dailyOrders;
        });
      } catch (error) {
        this.errorLoadingReport = true;
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    employee: {
      handler(newVal) {
        if (newVal) {
          this.loadReport();
        }
      },
      immediate: true,
    },
    date: {
      handler() {
        this.loadReport();
      },
    },
  },
};
</script>

<style scoped>
.v-data-table >>> tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05) !important;
}

table tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05) !important;
}

div.v-select__selections span.v-size--small span.v-chip__content {
  font-size: 10px !important;
}

.v-alert__icon.v-icon {
  font-size: 15px !important;
}

.order-history-header {
  background-color: rgb(255, 248, 245);
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2);
  padding: 10px;
  display: flex;
  align-content: center;
  height: 64px;
}

.v-input--checkbox {
  margin-top: 32px;
  padding-top: 0;
}
</style>

<style>
.show-all-checkbox .v-icon {
  margin-top: 4px !important;
}
</style>
